import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
    Divider,
    Grid,
} from '@mui/material';
import GenericModal from '../../../components/GenericModal';
import {
    CloudDownload as CloudDownloadIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
    clearFinanceInvoiceError,
    clearSelectedFinanceInvoice,
    downloadFinanceInvoice,
    fetchFinanceInvoice,
    fetchFinanceInvoices,
} from '../../../store/slices/financeInvoicesSlice';

interface InvoicesTabProps {
    selectedCustomerId?: number;
}

const InvoicesTab: React.FC<InvoicesTabProps> = ({ selectedCustomerId }) => {
    const dispatch = useAppDispatch();
    const invoicesState = useAppSelector((state) => state.financeInvoices);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    React.useEffect(() => {
        if (selectedCustomerId) {
            dispatch(fetchFinanceInvoices({ customerId: selectedCustomerId }));
        }
    }, [dispatch, selectedCustomerId]);

    const handleInvoiceDownload = async (invoiceId: number, uniqueId: string) => {
        try {
            const { blob } = await dispatch(downloadFinanceInvoice(invoiceId)).unwrap();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Invoice_${uniqueId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setErrorMessage((error as Error).message);
        }
    };

    return (
        <Card sx={{ overflow: 'hidden' }}>
            <CardHeader
                title="Invoices"
                action={
                    <IconButton
                        onClick={() => dispatch(fetchFinanceInvoices({ customerId: selectedCustomerId }))}
                    >
                        <RefreshIcon />
                    </IconButton>
                }
            />
            <CardContent>
                {invoicesState.error && (
                    <Alert severity="error" onClose={() => dispatch(clearFinanceInvoiceError())}>
                        {invoicesState.error}
                    </Alert>
                )}
                {errorMessage && (
                    <Alert severity="error" onClose={() => setErrorMessage(null)}>
                        {errorMessage}
                    </Alert>
                )}
                {invoicesState.loadingList && <CircularProgress />}
                {isMobile ? (
                    <Stack spacing={2}>
                        {invoicesState.list.map((invoice) => (
                            <Card key={invoice.id} variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle2">Invoice #{invoice.unique_id}</Typography>
                                        <Typography variant="subtitle2" fontWeight="bold">Rs. {invoice.service_fee_total?.toLocaleString() ?? '—'}</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 1 }} />
                                    <Grid container spacing={1}>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Type</Typography>
                                            <Typography variant="body2">{invoice.type}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Date</Typography>
                                            <Typography variant="body2">
                                                {invoice.date ? new Date(invoice.date).toLocaleDateString() : '—'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Customer</Typography>
                                            <Typography variant="body2">{invoice.customer?.name || '—'}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Consignments</Typography>
                                            <Typography variant="body2">{invoice.consignment_count?.toLocaleString() ?? '—'}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => dispatch(fetchFinanceInvoice(invoice.id))}
                                            >
                                                View
                                            </Button>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleInvoiceDownload(invoice.id, invoice.unique_id)}
                                            >
                                                <CloudDownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 800 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Invoice #</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Consignments</TableCell>
                                    <TableCell align="right">Service Fee</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoicesState.list.map((invoice) => (
                                    <TableRow key={invoice.id}>
                                        <TableCell>{invoice.unique_id}</TableCell>
                                        <TableCell>{invoice.customer?.name || '—'}</TableCell>
                                        <TableCell>{invoice.type}</TableCell>
                                        <TableCell align="right">
                                            {invoice.consignment_count?.toLocaleString() ?? '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            Rs.{invoice.service_fee_total?.toLocaleString() ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            {invoice.date ? new Date(invoice.date).toLocaleDateString() : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => dispatch(fetchFinanceInvoice(invoice.id))}
                                                >
                                                    View
                                                </Button>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleInvoiceDownload(invoice.id, invoice.unique_id)}
                                                >
                                                    <CloudDownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                <GenericModal
                    open={Boolean(invoicesState.selectedInvoice)}
                    onClose={() => dispatch(clearSelectedFinanceInvoice())}
                    title="Invoice Detail"
                    maxWidth="md"
                    fullWidth
                    actions={
                        <Button onClick={() => dispatch(clearSelectedFinanceInvoice())}>Close</Button>
                    }
                >
                    {invoicesState.loadingDetail && <CircularProgress />}
                    {invoicesState.selectedInvoice && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Invoice #{invoicesState.selectedInvoice.unique_id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {invoicesState.selectedInvoice.type} •{' '}
                                {invoicesState.selectedInvoice.customer?.name}
                            </Typography>

                            <Typography variant="subtitle2">Consignments</Typography>
                            {isMobile ? (
                                <Stack spacing={2} sx={{ mb: 2 }}>
                                    {invoicesState.selectedInvoice.consignments.map((consignment) => (
                                        <Card key={consignment.id} variant="outlined">
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="subtitle2">{consignment.consignment_no}</Typography>
                                                    <Typography variant="subtitle2" fontWeight="bold">Rs. {consignment.service_fee.toLocaleString()}</Typography>
                                                </Box>
                                                <Divider sx={{ mb: 1 }} />
                                                <Grid container spacing={1}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Status</Typography>
                                                        <Typography variant="body2">{consignment.status.name || '—'}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Tax</Typography>
                                                        <Typography variant="body2">Rs. {consignment.tax.toLocaleString()}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            ) : (
                                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                                    <Table size="small" sx={{ minWidth: 600 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Consignment</TableCell>
                                                <TableCell align="right">Service Fee</TableCell>
                                                <TableCell align="right">Tax</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {invoicesState.selectedInvoice.consignments.map((consignment) => (
                                                <TableRow key={consignment.id}>
                                                    <TableCell>{consignment.consignment_no}</TableCell>
                                                    <TableCell align="right">
                                                        Rs. {consignment.service_fee.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Rs. {consignment.tax.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell>{consignment.status.name || '—'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            <Typography variant="subtitle2">Notes</Typography>
                            {isMobile ? (
                                <Stack spacing={2}>
                                    {invoicesState.selectedInvoice.notes.map((note) => (
                                        <Card key={note.id} variant="outlined">
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="subtitle2">Note #{note.unique_id}</Typography>
                                                    <Typography variant="subtitle2" fontWeight="bold">Rs. {note.amount.toLocaleString()}</Typography>
                                                </Box>
                                                <Divider sx={{ mb: 1 }} />
                                                <Grid container spacing={1}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Type</Typography>
                                                        <Typography variant="body2">{note.type}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="text.secondary">Tax</Typography>
                                                        <Typography variant="body2">Rs. {note.tax.toLocaleString()}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table size="small" sx={{ minWidth: 500 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Note #</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                                <TableCell align="right">Tax</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {invoicesState.selectedInvoice.notes.map((note) => (
                                                <TableRow key={note.id}>
                                                    <TableCell>{note.unique_id}</TableCell>
                                                    <TableCell>{note.type}</TableCell>
                                                    <TableCell align="right">
                                                        Rs. {note.amount.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Rs. {note.tax.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Box>
                    )}
                </GenericModal>
            </CardContent>
        </Card>
    );
};

export default InvoicesTab;
